/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Settings, useSettings } from '@/data/Settings';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { ContentProps } from '@/data/types/ContentProps';
import { ProductType } from '@/data/types/Product';
import { isNil, omitBy } from 'lodash';
import { USPDeliveryTerms } from '../types/USPDeliveryTerms';
import { getLocalization } from '@/data/Localization';
import { findAttributeByIdentifier, getAttributeValueByIdentifier } from '@/utils/productAttributes';
import { useProduct } from '@/data/Content/Product';

const dataMap = (data: ProductType | undefined, settings: Settings): USPDeliveryTerms => {
	const buyable = data?.buyable;
	const offerPrice = data?.price?.find(o => o.usage === 'Offer')?.value;
	const checkPrice = offerPrice !== '' ? parseFloat(offerPrice??'0') : '0'; 
	const attrs = data?.attributes;
	const msgDeliveryTerm = getAttributeValueByIdentifier(attrs, 'ITNL_DeliveryTerm');
	const itnlPickupInStore = findAttributeByIdentifier(attrs, 'ITNL_PickupInStore');
	const pickUpInStoreAllowed = itnlPickupInStore?.values?.at(0)?.identifier === 'true'
		&& itnlPickupInStore?.usage === 'Descriptive';
	const preSaleCatentry = getAttributeValueByIdentifier(attrs, 'ITNL_DeliveryTerm') ? true : false;
	const {
		orderCheckoutMinimum,
		orderFreeShippingMinimum,
		orderActualCheckoutMinimum,
		orderActualFreeShippingMinimum,
		StaticHostSecure
	} = settings.userData;
	
	const orderShippingMinimumFinal = parseFloat(orderCheckoutMinimum ?? '0');
	const orderFreeShippingMinimumFinal = parseFloat(orderFreeShippingMinimum ?? '0');
	const orderShippingMinimumActualFinal = parseFloat(orderActualCheckoutMinimum ?? '0');
	const orderFreeShippingMinimumActualFinal = parseFloat(orderActualFreeShippingMinimum ?? '0');
	const uspDeliveryTerms = {
		checkPrice,
		msgDeliveryTerm,
		pickUpInStoreAllowed,
		preSaleCatentry,
		hostURL: StaticHostSecure ?? '',
		orderShippingMinimumFinal,
		orderFreeShippingMinimumActualFinal,
		orderFreeShippingMinimumFinal,
		orderShippingMinimumActualFinal,
		buyable,
	}
	return omitBy(uspDeliveryTerms, isNil) as USPDeliveryTerms;
};

export const getUSPsDeliveryTerms = async ({ cache, id: _id, context }: ContentProps) => {
	await getLocalization(cache, context.locale || 'nl-NL', 'USPDeliveryTerms');
};

export const useUSPsDeliveryTerms = () => {
	const { settings } = useSettings();
	const { data: pageData } = usePageDataFromId();
	const { tokenValue = '', tokenExternalValue = '' } = pageData ?? {};
	const { product } = useProduct({ id: tokenExternalValue });
    const uspsDeliveryTerms = dataMap(product, settings)
    return {
        uspsDeliveryTerms,
        uniqueId: tokenValue,
    };
};